import classNames from 'classnames'
import type { ChangeEvent, FC, FocusEvent } from 'react'
import { useCallback, useRef } from 'react'
import { Popup } from '../../components/popup'
import { getAccessibilityTags } from '../../config'
import { useDeviceMedia } from '../../hooks/use-device-media'
import { IconButton, IconButtonSize, IconButtonVariants } from '../button'
import { useDropdown } from '../dropdown/hooks'
import { CrossIcon } from '../icon'
import { SearchInput, SearchSize } from '../inputs'
import { BaseModal, BaseModalContent, BaseModalHeader } from '../modal'
import styles from './DropdownSearch.module.scss'
import type { DropdownSearchProps } from './types'

const DropdownSearch: FC<DropdownSearchProps> = ({
  ariaLabelCloseButton,
  children,
  className,
  closeOnClickOutside = true,
  isOpen = false,
  onClose,
  onFocus,
  onChange,
  ...props
}) => {
  const { isDesktop } = useDeviceMedia()
  const onFocusMobileTimeoutRef = useRef<NodeJS.Timeout>()
  const inputModalRef = useRef<HTMLInputElement>(null)
  const { handleClose: handleDropdownClose, onClickOutside } = useDropdown({
    closeOnClickOutside,
    isOpen,
    onClose,
  })
  const handleOnChangeInMobile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(event)
    },
    [onChange]
  )

  const handleOnFocusInMobile = useCallback(
    (event: FocusEvent<HTMLInputElement, Element>) => {
      onFocus && onFocus(event)
      onFocusMobileTimeoutRef && clearTimeout(onFocusMobileTimeoutRef.current)
      onFocusMobileTimeoutRef.current = setTimeout(() => {
        inputModalRef.current?.focus()
      }, 480)
    },
    [onFocus, inputModalRef]
  )
  
  const { close: closeAriaLabel } = getAccessibilityTags()

  return (
    <div className={classNames(styles['pintxo-dropdown-search'], className)}>
      {isDesktop ? (
        <Popup
          trigger={
            <SearchInput
              className={styles['pintxo-dropdown-search__search-input']}
              onFocus={onFocus}
              onChange={onChange}
              {...props}
            />
          }
          onClickOutside={onClickOutside}
          isOpen={isOpen}
        >
          {children}
        </Popup>
      ) : (
        <div className={styles['pintxo-dropdown-search']}>
          <SearchInput
            className={styles['pintxo-dropdown-search__search-input']}
            onFocus={handleOnFocusInMobile}
            onChange={undefined}
            {...props}
          />
          <BaseModal
            className={styles['pintxo-dropdown-search__modal']}
            isOpen={isOpen}
            showCloseButton={false}
            overlayClose={false}
            fullscreen={true}
          >
            <BaseModalHeader>
              {
                <div className={styles['pintxo-dropdown-search__header-container']}>
                  <SearchInput
                    {...props}
                    className={styles['pintxo-dropdown-search__search-input']}
                    onFocus={undefined}
                    onChange={handleOnChangeInMobile}
                    ref={inputModalRef}
                    size={SearchSize.small}
                  />
                  <IconButton
                    aria-label={ariaLabelCloseButton || closeAriaLabel}
                    variant={IconButtonVariants.neutral}
                    size={IconButtonSize.small}
                    onClick={handleDropdownClose}
                    icon={CrossIcon}
                  />
                </div>
              }
            </BaseModalHeader>
            <BaseModalContent>{children}</BaseModalContent>
          </BaseModal>
        </div>
      )}
    </div>
  )
}

export default DropdownSearch
